"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, Shield, Crosshair, User, Activity, Send, CircleCheck, CircleAlert } from "lucide-react";
function InquiryFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 px-6 text-center bg-card/40 backdrop-blur-xl border border-border/50 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-primary"></div>
        <CircleCheck className="size-16 text-primary mb-6" />
        <h3 className="text-2xl font-bold text-foreground mb-2">Profile Submitted</h3>
        <p className="text-muted-foreground max-w-md mb-8">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-primary/20 hover:bg-primary/10">
          Submit Another Profile
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="bg-card/60 backdrop-blur-xl border border-border/50 shadow-2xl relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/50 via-primary to-primary/50"></div>
      
      {}
      <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/5 rotate-45 blur-2xl pointer-events-none"></div>
      <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-primary/5 rotate-45 blur-2xl pointer-events-none"></div>

      <div className="p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-10 relative z-10">
        {}
        <div className="space-y-6">
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-foreground flex items-center gap-2 tracking-tight">
              <User className="size-5 text-primary" />
              Personal Details
            </h3>
            <Separator className="bg-border/60" />
          </div>

          <div className="space-y-5">
            <FormField control={control} name="name" rules={{
            required: "Player name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-bold">Player Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. John Doe" className="bg-input/40 border-border/50 focus-visible:border-primary" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <FormField control={control} name="email" rules={{
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email address"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-bold">Email Address</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="player@example.com" className="bg-input/40 border-border/50 focus-visible:border-primary" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="phone" rules={{
              required: "Phone number is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-bold">Phone Number</FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="(555) 000-0000" className="bg-input/40 border-border/50 focus-visible:border-primary" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <FormField control={control} name="location" rules={{
              required: "Location is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-bold">City, State/Prov</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Boston, MA" className="bg-input/40 border-border/50 focus-visible:border-primary" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="birthYear" rules={{
              required: "Birth year is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-bold">Birth Year</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="YYYY" className="bg-input/40 border-border/50 focus-visible:border-primary" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>
        </div>

        {}
        <div className="space-y-6">
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-foreground flex items-center gap-2 tracking-tight">
              <Activity className="size-5 text-primary" />
              Athletic Profile
            </h3>
            <Separator className="bg-border/60" />
          </div>

          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-5">
              <FormField control={control} name="height" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-bold">Height</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. 6'1&quot;" className="bg-input/40 border-border/50 focus-visible:border-primary" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="weight" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-bold">Weight (lbs)</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. 185" className="bg-input/40 border-border/50 focus-visible:border-primary" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="grid grid-cols-2 gap-5">
              <FormField control={control} name="position" rules={{
              required: "Position is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-bold">Position</FormLabel>
                    <FormControl>
                      <Input placeholder="F / D / G" className="bg-input/40 border-border/50 focus-visible:border-primary" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="shoots" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-bold">Shoots/Catches</FormLabel>
                    <FormControl>
                      <Input placeholder="Left / Right" className="bg-input/40 border-border/50 focus-visible:border-primary" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <FormField control={control} name="currentTeam" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-bold">Current Team & League</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Boston Jr. Bruins (NCDC)" className="bg-input/40 border-border/50 focus-visible:border-primary" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="links" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-bold">Elite Prospects / Video Link</FormLabel>
                  <FormControl>
                    <Input type="url" placeholder="https://" className="bg-input/40 border-border/50 focus-visible:border-primary" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        {}
        <div className="col-span-1 md:col-span-2 space-y-6 mt-4">
          <Separator className="bg-border/60" />
          
          <FormField control={control} name="message" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs font-bold">Additional Information / Goals</FormLabel>
                <FormControl>
                  <textarea className="flex min-h-[100px] w-full rounded-md border border-border/50 bg-input/40 px-3 py-2 text-base shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/50 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-y" placeholder="Tell us about your goals (Junior, College) or any questions you have..." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
              <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
              <p className="text-sm text-destructive-foreground">{submitError}</p>
            </div>}

          <Button type="submit" disabled={isSubmitting} size="lg" className="w-full text-base font-bold tracking-wide uppercase h-14 shadow-[0_0_20px_rgba(var(--primary),0.3)] hover:shadow-[0_0_30px_rgba(var(--primary),0.5)] transition-all duration-300">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-5" />
                Processing Profile...
              </> : <>
                <Send className="mr-2 size-5" />
                Submit Profile for Review
              </>}
          </Button>
          <p className="text-center text-xs text-muted-foreground mt-4">
            Your information is secure and will only be reviewed by DB Hockey Advancement staff.
          </p>
        </div>
      </div>
    </Form>;
}
export default function Content() {
  return;
}